/**
 * /admin/platform/branding — edit platform-wide branding.
 *
 * Covers product name, marketing URL, support contact, default
 * tenant theme JSON, and logo + favicon upload. Saving any field
 * triggers the MFA prompt (server enforces it on the PUT route).
 */

import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  adminApi,
  type AdminApiError,
  type PlatformBrandingPayload,
} from "./api";
import { MfaPrompt } from "./MfaPrompt";

const EMPTY: PlatformBrandingPayload = {
  product_name: "",
  marketing_url: null,
  root_logo_url: null,
  default_support_contact: null,
  default_tenant_theme: null,
};

export function PlatformBrandingPage() {
  const [data, setData] = useState<PlatformBrandingPayload>(EMPTY);
  const [themeJson, setThemeJson] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<{
    kind: "logo" | "favicon";
    file: File;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const faviconInput = useRef<HTMLInputElement>(null);

  async function reload() {
    try {
      const result = await adminApi.getPlatformBranding();
      setData(result);
      setThemeJson(
        result.default_tenant_theme === null
          ? ""
          : JSON.stringify(result.default_tenant_theme, null, 2),
      );
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not load branding.");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setPendingSave(true);
  }

  async function executeSave(code: string) {
    let theme: Record<string, string> | null = null;
    if (themeJson.trim().length > 0) {
      try {
        theme = JSON.parse(themeJson) as Record<string, string>;
      } catch {
        setPendingSave(false);
        setError("Default tenant theme is not valid JSON.");
        return;
      }
    }
    try {
      const result = await adminApi.putPlatformBranding(
        { ...data, default_tenant_theme: theme },
        code,
      );
      setData(result);
      setPendingSave(false);
      setInfo("Platform branding saved.");
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      throw new Error(apiErr.message ?? "Save failed.");
    }
  }

  async function executeUpload(code: string) {
    if (pendingUpload === null) {
      return;
    }
    try {
      const result = await adminApi.uploadPlatformAsset(
        pendingUpload.kind,
        pendingUpload.file,
        code,
      );
      setData(result);
      setThemeJson(
        result.default_tenant_theme === null
          ? ""
          : JSON.stringify(result.default_tenant_theme, null, 2),
      );
      setPendingUpload(null);
      setInfo(`${pendingUpload.kind === "logo" ? "Logo" : "Favicon"} uploaded.`);
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      throw new Error(apiErr.message ?? "Upload failed.");
    }
  }

  function chooseFile(kind: "logo" | "favicon") {
    const input = kind === "logo" ? logoInput.current : faviconInput.current;
    input?.click();
  }

  function onFileSelected(kind: "logo" | "favicon", input: HTMLInputElement) {
    if (input.files && input.files.length > 0) {
      setPendingUpload({ kind, file: input.files[0] });
      input.value = "";
    }
  }

  if (!loaded) {
    return <p className="field-hint">Loading…</p>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>Platform branding</h1>
        <p className="admin-page-sub">
          The defaults that every tenant inherits. Tenant admins can
          override individual tokens; anything they leave blank falls
          through to these values.
        </p>
      </header>

      {error !== null && <div className="auth-error" role="alert">{error}</div>}
      {info !== null && <div className="auth-info">{info}</div>}

      <form className="admin-card" onSubmit={onSubmit} noValidate>
        <h2 className="admin-card-title">Identity</h2>
        <div className="field">
          <label htmlFor="pb-name">Product name</label>
          <input
            id="pb-name"
            type="text"
            value={data.product_name}
            onChange={(e) => setData({ ...data, product_name: e.target.value })}
            required
            maxLength={200}
          />
        </div>
        <div className="field">
          <label htmlFor="pb-marketing">Marketing URL</label>
          <input
            id="pb-marketing"
            type="url"
            value={data.marketing_url ?? ""}
            onChange={(e) =>
              setData({
                ...data,
                marketing_url: e.target.value.trim() || null,
              })
            }
            placeholder="https://example.com"
          />
        </div>
        <div className="field">
          <label htmlFor="pb-support">Default support contact</label>
          <input
            id="pb-support"
            type="email"
            value={data.default_support_contact ?? ""}
            onChange={(e) =>
              setData({
                ...data,
                default_support_contact: e.target.value.trim() || null,
              })
            }
            placeholder="support@example.com"
          />
        </div>
        <div className="field">
          <label htmlFor="pb-theme">
            Default tenant theme (JSON)
          </label>
          <textarea
            id="pb-theme"
            value={themeJson}
            onChange={(e) => setThemeJson(e.target.value)}
            rows={6}
            placeholder='{ "primary_colour": "#0a84ff", "favicon_url": "https://..." }'
          />
          <div className="field-hint">
            Token names match the tenant branding form. Tenant overrides
            win over these defaults at render time.
          </div>
        </div>
        <div className="actions">
          <button type="submit" className="btn btn-primary btn-sm">
            Save platform branding
          </button>
        </div>
      </form>

      <div className="admin-card">
        <h2 className="admin-card-title">Brand artwork</h2>
        <div className="brand-asset-grid">
          <div className="brand-asset">
            <div className="brand-asset-label">Logo</div>
            <div className="brand-asset-preview">
              {data.root_logo_url ? (
                <img src={data.root_logo_url} alt="Platform logo" />
              ) : (
                <div className="brand-asset-empty">No logo set</div>
              )}
            </div>
            <input
              ref={logoInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              hidden
              onChange={(e) => onFileSelected("logo", e.currentTarget)}
            />
            <div className="actions">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => chooseFile("logo")}
              >
                Upload logo…
              </button>
            </div>
          </div>

          <div className="brand-asset">
            <div className="brand-asset-label">Favicon</div>
            <div className="brand-asset-preview brand-asset-favicon">
              {data.default_tenant_theme?.favicon_url ? (
                <img
                  src={data.default_tenant_theme.favicon_url}
                  alt="Platform favicon"
                />
              ) : (
                <div className="brand-asset-empty">No favicon set</div>
              )}
            </div>
            <input
              ref={faviconInput}
              type="file"
              accept="image/png,image/x-icon,image/svg+xml"
              hidden
              onChange={(e) => onFileSelected("favicon", e.currentTarget)}
            />
            <div className="actions">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => chooseFile("favicon")}
              >
                Upload favicon…
              </button>
            </div>
          </div>
        </div>
        <p className="field-hint">
          PNG, JPEG, WebP, or SVG, up to 2&nbsp;MiB. The asset is stored
          locally for now and served via /v1/branding/assets/&hellip; The
          same component will swap to Cloudflare R2 / Cloudflare Images
          on production deploy without route changes.
        </p>
      </div>

      <MfaPrompt
        open={pendingSave}
        title="Confirm platform branding change"
        description="Editing platform-wide branding is a sensitive action. Enter your authenticator code to continue."
        onCancel={() => setPendingSave(false)}
        onConfirm={executeSave}
      />
      <MfaPrompt
        open={pendingUpload !== null}
        title={
          pendingUpload === null
            ? ""
            : `Upload platform ${pendingUpload.kind}`
        }
        description="Brand-asset uploads are a sensitive action. Enter your authenticator code to continue."
        onCancel={() => setPendingUpload(null)}
        onConfirm={executeUpload}
      />
    </div>
  );
}
